import React, { useContext, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import Uppy from '@uppy/core'
import RemoteSources from '@uppy/remote-sources'
import AwsS3 from '@uppy/aws-s3'

import { DashboardModal } from '@uppy/react'
import FirebaseStorageUpload from '../../../utils/FirebaseStorageUpload'
import { useCustomFetch } from '@/hooks/useCustomFetch'

import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import './importFiles.css'
import { useNavigate } from 'react-router-dom'
import { RemoteConfigContext } from '@/context/RemoteConfigContext'
import { createPortal } from 'react-dom'
// Import storage from firebase.js
import { storage } from '../../../firebase'
// Import Firebase storage functions
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'

const MAX_FILE_SIZE_IN_BYTES = 2 * 1024 * 1024 * 1024 // 2gb

const ALLOWED_FILE_TYPES = [
  'audio/mpeg',
  'audio/mp4',
  'audio/mp2',
  'audio/wav',
  'audio/flac',
  'audio/x-flac',
  'audio/l16',
  'audio/x-m4a',
  'audio/ogg',
  'audio/opus',
  'audio/webm',
  'video/mp4',
  'video/webm',
]

interface ImportFilesProps {
  projectId: string
  projectName: string
  handleClose: (...args: any[]) => void
  setMeetings?: (...args: any[]) => void
  limitFiles?: number
  meetingId?: string
  handleGoBack?: (...args: any[]) => void
  customFile?: File & { duration?: number; fileName?: string }
  customNote?: string
  skipNavigation?: boolean // If true, won't navigate to project page after upload
}

export function ImportFiles({
  projectId,
  projectName,
  handleClose,
  setMeetings,
  limitFiles = 10,
  meetingId,
  handleGoBack,
  customFile,
  customNote,
  skipNavigation = false,
}: ImportFilesProps) {
  const navigate = useNavigate()
  const customFetch = useCustomFetch()
  const { config, isLoading: remoteConfigLoading } = useContext(RemoteConfigContext)

  const [dashboardNode, setDashboardNode] = useState(null)

  const remoteSources = []

  if (!remoteConfigLoading) {
    if (config.googleDrive) remoteSources.push('GoogleDrive')
    if (config.oneDrive) remoteSources.push('OneDrive')
    if (config.zoom) remoteSources.push('Zoom')
    if (config.dropbox) remoteSources.push('Dropbox')
    if (config.box) remoteSources.push('Box')
  }

  const [uppy] = useState(() => {
    const newUppy = new Uppy({
      autoProceed: false,
      debug: true,
      restrictions: {
        maxFileSize: MAX_FILE_SIZE_IN_BYTES,
        maxNumberOfFiles: limitFiles || 10,
        allowedFileTypes: ALLOWED_FILE_TYPES,
      },
    })
      .use(RemoteSources, {
        companionUrl: import.meta.env.VITE_COMPANION_URL,
        sources: remoteSources,
      })
      .use(FirebaseStorageUpload, {})

    newUppy.on('file-added', (file) => {
      if (file.source && ['GoogleDrive', 'Zoom', 'Dropbox', 'OneDrive'].includes(file.source)) {
        newUppy.use(AwsS3, {
          endpoint: import.meta.env.VITE_COMPANION_URL,
        })
      } else {
        if (newUppy.getPlugin(AwsS3.name)) {
          newUppy.removePlugin(AwsS3 as any)
        }
      }
    })

    return newUppy
  })

  useEffect(() => {
    uppy.on('complete', async (result) => {
      try {
        // console.log('Successful uploads:', result.successful)
        if (result.successful.length) {
          const files = []
          for (const file of result.successful) {
            if (file.isRemote) {
              let url = ''
              try {
                const storedFileName = file.uploadURL.split('/').pop()
                const res = await customFetch(`/getMeetUrlByStoredName/${storedFileName}`, 'GET', null)
                if (res.error) throw res
                url = res.url
              } catch (error) {
                console.error('Error getting file URL:', error)
                continue
              }
              const duration = await new Promise((resolve, reject) => {
                const mediaElement = document.createElement(file.type.startsWith('audio') ? 'audio' : 'video')
                mediaElement.src = url
                mediaElement.addEventListener('loadedmetadata', () => {
                  resolve(mediaElement.duration) // duration in seconds
                })
                mediaElement.addEventListener('error', reject)
              })

              let fileLocation: string | undefined = file?.response?.body?.Key
              if (!fileLocation) fileLocation = ''
              // removing folder meets/ to get only the file name
              const storedFileName = fileLocation.substring(6)

              files.push({ url, fileName: file.name, size: file.size, storedFileName, duration, type: file.type })
            } else {
              const { url, storedFileName, duration } = JSON.parse(file.uploadURL)
              files.push({
                url,
                fileName: file.name,
                size: file.size,
                storedFileName,
                duration,
                type: file.type,
              })
            }
          }
          if (!files.length) return

          const start = Date.now()
          const res = await customFetch('/createNotesFromFiles', 'POST', {
            projectId,
            files: JSON.stringify(files),
            meetingId,
          })
          if (res.error) {
            toast.error(res.error.msg)
            handleClose()
            return
          }
          if (!res.meetings.length) {
            toast.error('Whoops! Something went wrong. Please try again.')
            handleClose()
            return
          }

          toast.success("Files uploaded! We'll let you know when the notes are ready. You may close your browser.", {
            duration: 3000,
          })
          // the sorting is added just in case there is an event with a future meet that should go first
          if (setMeetings && !meetingId) {
            setMeetings((prevMeetings) =>
              [...res.meetings, ...prevMeetings].sort((a, b) => {
                let timeA = a.created_at
                let timeB = b.created_at
                if (a.start) {
                  timeA = new Date(a.start).getTime()
                }
                if (b.start) {
                  timeB = new Date(b.start).getTime()
                }
                return timeA < timeB ? 1 : -1
              })
            )
          }
          if (setMeetings && meetingId) {
            setMeetings((prevMeetings) =>
              prevMeetings
                .map((meet) => (meet.id === meetingId ? res.meetings[0] : meet))
                .sort((a, b) => {
                  let timeA = a.created_at
                  let timeB = b.created_at
                  if (a.start) {
                    timeA = new Date(a.start).getTime()
                  }
                  if (b.start) {
                    timeB = new Date(b.start).getTime()
                  }
                  return timeA < timeB ? 1 : -1
                })
            )
          }
          const elapsed = Date.now() - start
          if (elapsed < 3000) {
            await new Promise((resolve) => setTimeout(resolve, 3000 - elapsed))
          }
          if (!skipNavigation) {
            navigate(`/project/${projectId}`)
          }
          handleClose()
        }
      } catch (error) {
        console.error('Error uploading files', error)
        toast.error('Whoops! Something went wrong. Please try again.')
        handleClose()
      }
    })

    return () => uppy.destroy()
  }, [])

  useEffect(() => {
    // Dynamically find the node with the class `uppy-Dashboard-AddFiles`
    const node = document.querySelector('.uppy-Dashboard-AddFiles')
    if (node) {
      setDashboardNode(node)
    }
  }, [])

  // Handle recording file upload
  const handleRecordedFile = async (file: File & { duration?: number; fileName?: string }) => {
    try {
      if (!file || !file.duration) return

      const storedFileName = `recording-${Date.now()}.${file.name.split('.').pop()}`
      const storageRef = ref(storage, `meets/${storedFileName}`)

      // Upload file to Firebase Storage
      const uploadTask = uploadBytesResumable(storageRef, file)

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Monitor upload progress if needed
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          console.log('Upload is ' + progress + '% done')
        },
        (error) => {
          console.error('Upload error:', error)
          toast.error('Error uploading recording. Please try again.')
          handleClose()
        },
        async () => {
          // Handle successful upload
          try {
            // Get the download URL
            const url = await getDownloadURL(storageRef)

            // Process the file like normal uploads
            const fileData = {
              url,
              fileName: file.fileName || file.name,
              size: file.size,
              storedFileName,
              duration: file.duration,
              type: file.type,
            }

            const start = Date.now()
            const res = await customFetch('/createNotesFromFiles', 'POST', {
              projectId,
              files: JSON.stringify([fileData]),
              meetingId,
            })

            if (res.error) {
              toast.error(res.error.msg)
              handleClose()
              return
            }

            if (!res.meetings || !res.meetings.length) {
              toast.error('Whoops! Something went wrong. Please try again.')
              handleClose()
              return
            }

            toast.success("Recording uploaded! We'll let you know when the notes are ready.", {
              duration: 3000,
            })

            // Handle meetings update similar to existing code
            if (setMeetings && !meetingId) {
              setMeetings((prevMeetings) =>
                [...res.meetings, ...prevMeetings].sort((a, b) => {
                  let timeA = a.created_at
                  let timeB = b.created_at
                  if (a.start) {
                    timeA = new Date(a.start).getTime()
                  }
                  if (b.start) {
                    timeB = new Date(b.start).getTime()
                  }
                  return timeA < timeB ? 1 : -1
                })
              )
            }

            const elapsed = Date.now() - start
            if (elapsed < 3000) {
              await new Promise((resolve) => setTimeout(resolve, 3000 - elapsed))
            }

            if (!skipNavigation) {
              navigate(`/project/${projectId}`)
            }
            handleClose()
          } catch (error) {
            console.error('Error processing file:', error)
            toast.error('Whoops! Something went wrong. Please try again.')
            handleClose()
          }
        }
      )
    } catch (error) {
      console.error('Error handling recorded file:', error)
      toast.error('Whoops! Something went wrong. Please try again.')
      handleClose()
    }
  }

  useEffect(() => {
    // If we have a custom file from recording, process it directly
    if (customFile && customFile.duration) {
      handleRecordedFile(customFile)
    }
  }, [customFile])

  const isOnlyOne = limitFiles === 1

  const text = isOnlyOne ? 'Upload one file at a time up to 2GB' : `Upload up to ${limitFiles} files, each up to 2GB`

  return (
    <>
      {handleGoBack &&
        dashboardNode &&
        (createPortal(
          <button
            className="absolute top-4 right-4 mr-[18px] px-4 py-2.5 rounded-[100px] shadow-inner border border-pink justify-center items-center gap-3 inline-flex"
            onClick={handleGoBack}
          >
            <p className="text-pink text-base font-semibold font-['Manrope'] leading-tight">Go back</p>
          </button>,
          dashboardNode
        ) as React.ReactPortal)}
      {!customFile && (
        <DashboardModal
          uppy={uppy}
          open={true}
          target={document.body as any}
          note={customNote || `${text}. Acceptable file types: MP3, MP4, MP2, WAV, FLAC, PCM, M4A, Ogg, Opus, WebM.`}
          plugins={remoteSources}
          onRequestClose={() => handleClose(false)}
        />
      )}
    </>
  )
}
